/**
 * Home's day strip (D354): seven days from today, a dot for each kind of thing due, and the two
 * chips at the end — Needs you and Routines. Pressing a day filters the feed to it (today is
 * pressed by default); the Needs you chip never filters, it opens the Inbox; the Routines chip
 * opens the Routines view's Week calendar. Today is marked by a fill, a dot and the word
 * "Today" for a screen reader, not by colour alone.
 *
 * Ported from the workbench's components/home/HomeDayStrip.tsx: the Routines chip shows only where routines are
 * known.
 */
import { Clock, Persons, Tray } from '@gravity-ui/icons';
import { Button } from '@heroui/react';

import { type DayCell, DOT_FACTS, dotWords, needsYouChipWords, routinesChipWords } from '../../sharedHome.js';
import { Hint } from '../ui/Hint.js';

export interface HomeDayStripProps {
  cells: readonly DayCell[];
  selected: number;
  onSelect: (start: number) => void;
  needsYou: number;
  /** Absent where routines are not known (the console: they live on the desktop), and the chip is left out. */
  routinesToday?: number;
  onOpenInbox: () => void;
  onOpenRoutines?: () => void;
  /** The Team chip, on a team or organisation project only. */
  team?: { words: string };
  onOpenTeam?: () => void;
}

function dayHint(cell: DayCell): string {
  return cell.dots.length ? cell.dots.map(dotWords).join('\n') : 'Nothing scheduled';
}

export function HomeDayStrip({ cells, selected, onSelect, needsYou, routinesToday, onOpenInbox, onOpenRoutines, team, onOpenTeam }: HomeDayStripProps) {
  const day = cells.find((c) => c.start === selected) ?? cells[0];
  return (
    <section className="home-strip" aria-label="The week ahead" data-home-strip>
      <div className="home-strip__bar glass-strip">
        <div className="home-strip__days" role="group" aria-label="Days">
          {cells.map((cell) => {
            const isSelected = cell.start === day.start;
            return (
              <Hint key={cell.start} text={<span className="whitespace-pre-line">{dayHint(cell)}</span>} placement="bottom">
                <Button
                  variant="ghost"
                  className="home-strip__day"
                  aria-pressed={isSelected}
                  aria-label={cell.label}
                  aria-current={cell.isToday ? 'date' : undefined}
                  data-today={cell.isToday || undefined}
                  data-selected={isSelected || undefined}
                  data-day={cell.start}
                  onPress={() => onSelect(cell.start)}
                >
                  <span className="home-strip__name">
                    {cell.isToday ? 'Today' : cell.weekday} <b>{cell.date}</b>
                  </span>
                  <span className="home-strip__dots" aria-hidden="true">
                    {cell.dots.map((d) => (
                      <i key={d.kind} data-dot={d.kind} data-tone={DOT_FACTS[d.kind].tone} />
                    ))}
                  </span>
                </Button>
              </Hint>
            );
          })}
        </div>
        <span className="home-strip__gap" />
        <Button variant="ghost" className="home-strip__chip" onPress={onOpenInbox} data-home-chip="needs-you">
          <Tray className="size-[18px]" />
          <span className="home-strip__chip-text">
            <span className="home-strip__chip-title">Needs you</span>
            <span className={`home-strip__chip-detail ${needsYou ? 'home-strip__chip-detail--needs-you' : ''}`}>{needsYouChipWords(needsYou)}</span>
          </span>
        </Button>
        {routinesToday !== undefined ? (
          <Button variant="ghost" className="home-strip__chip" onPress={onOpenRoutines} data-home-chip="routines">
            <Clock className="size-[18px]" />
            <span className="home-strip__chip-text">
              <span className="home-strip__chip-title">Routines</span>
              <span className="home-strip__chip-detail">{routinesChipWords(routinesToday)}</span>
            </span>
          </Button>
        ) : null}
        {team ? (
          <Button variant="ghost" className="home-strip__chip" onPress={onOpenTeam} data-home-chip="team">
            <Persons className="size-[18px]" />
            <span className="home-strip__chip-text">
              <span className="home-strip__chip-title">Team</span>
              <span className="home-strip__chip-detail">{team.words}</span>
            </span>
          </Button>
        ) : null}
      </div>
      <p className="home-strip__summary" aria-live="polite" data-home-strip-summary>
        {day.isToday ? 'Today' : day.label.split(':')[0]}: {day.summary}
      </p>
    </section>
  );
}
